import { useCallback, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrown, faMedal, faReceipt, faSackDollar } from '@fortawesome/free-solid-svg-icons';
import { Button, DatePicker, Popover } from '@shopify/polaris';
import { CalendarIcon } from '@shopify/polaris-icons';

import 'media/css/BangXepHangNhanvienTheoThang.scss';
import { useGetThisMonthlyStaffRank } from 'queries/analytics.query';
import Capacity from 'components/Capacity';
import __helpers from 'helpers/index';

export interface MonthlyStaffRankItem {
  user_id: number | string;
  display_name: string;
  user_avatar?: string | null;
  totalOrders: number;
  totalRevenue: number;
}

interface MonthlyRankingRow extends MonthlyStaffRankItem {
  rank: number;
  isPlaceholder?: boolean;
}

const EMPTY_PLACEHOLDERS: MonthlyRankingRow[] = [
  {
    user_id: 'placeholder-rank-1',
    display_name: 'Nhân viên nổi bật',
    totalOrders: 0,
    totalRevenue: 0,
    rank: 1,
    isPlaceholder: true,
  },
  {
    user_id: 'placeholder-rank-2',
    display_name: 'Nhân viên nổi bật',
    totalOrders: 0,
    totalRevenue: 0,
    rank: 2,
    isPlaceholder: true,
  },
  {
    user_id: 'placeholder-rank-3',
    display_name: 'Nhân viên nổi bật',
    totalOrders: 0,
    totalRevenue: 0,
    rank: 3,
    isPlaceholder: true,
  },
];

const numberFormatter = new Intl.NumberFormat('vi-VN');

const currencyFormatter = new Intl.NumberFormat('vi-VN', {
  style: 'currency',
  currency: 'VND',
  maximumFractionDigits: 0,
});

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);

  if (words.length === 0) {
    return 'NV';
  }

  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }

  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
}

function getRowClassName(item: MonthlyRankingRow): string {
  const classNames = ['monthly-staff-ranking__row'];

  if (item.rank && item.rank <= 3) {
    classNames.push(`monthly-staff-ranking__row--rank-${item.rank}`);
  }

  if (item.isPlaceholder) {
    classNames.push('monthly-staff-ranking__row--placeholder');
  }

  return classNames.join(' ');
}

function RankBadge({ rank }: { rank: number }) {
  if (rank === 1) {
    return (
      <div className="monthly-staff-ranking__rank monthly-staff-ranking__rank--first">
        <FontAwesomeIcon icon={faCrown} />
        <span>1</span>
      </div>
    );
  }

  if (rank === 2 || rank === 3) {
    return (
      <div className={['monthly-staff-ranking__rank', `monthly-staff-ranking__rank--${rank === 2 ? 'second' : 'third'}`].join(' ')}>
        <FontAwesomeIcon icon={faMedal} />
        <span>{rank}</span>
      </div>
    );
  }

  return <div className="monthly-staff-ranking__rank monthly-staff-ranking__rank--normal">{rank}</div>;
}

export default function MonthlyStaffRanking() {
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [calendarDate, setCalendarDate] = useState(() => ({
    month: selectedDate.getMonth(),
    year: selectedDate.getFullYear(),
  }));
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const { data: monthlyStaffRank = [], isLoading } = useGetThisMonthlyStaffRank({
    month: selectedDate.getMonth() + 1,
    year: selectedDate.getFullYear(),
  });

  const handleMonthChange = useCallback((month: number, year: number) => {
    setCalendarDate({ month, year });
  }, []);

  const handleDateChange = useCallback(({ start }: { start: Date; end: Date }) => {
    setSelectedDate(start);
    setCalendarDate({ month: start.getMonth(), year: start.getFullYear() });
    setIsCalendarOpen(false);
  }, []);

  const selectedMonthLabel = `Tháng ${selectedDate.getMonth() + 1}/${selectedDate.getFullYear()}`;

  const rankingRows = useMemo<MonthlyRankingRow[]>(() => {
    if (isLoading || monthlyStaffRank.length === 0) {
      return EMPTY_PLACEHOLDERS;
    }

    const sortedData = [...monthlyStaffRank].sort((first, second) => {
      if (second.totalRevenue !== first.totalRevenue) {
        return second.totalRevenue - first.totalRevenue;
      }

      if (second.totalOrders !== first.totalOrders) {
        return second.totalOrders - first.totalOrders;
      }

      return first.display_name.localeCompare(second.display_name, 'vi');
    });

    return sortedData.map<MonthlyRankingRow>((staff, index) => ({
      ...staff,
      rank: index + 1,
    }));
  }, [isLoading, monthlyStaffRank]);

  const getAvatarSource = (avatar?: string | null) => {
    return avatar ? __helpers.getMediaLink(avatar) : undefined;
  };

  return (
    <Capacity current_user_can="view_staff_rank_widget" onFail={null}>
      <section className="monthly-staff-ranking">
        <header className="monthly-staff-ranking__header">
          <div>
            <div className="monthly-staff-ranking__eyebrow">Thành tích theo tháng</div>

            <h2 className="monthly-staff-ranking__title">Bảng xếp hạng nhân viên</h2>
          </div>

          <div className="monthly-staff-ranking__summary">
            <Popover
              active={isCalendarOpen}
              activator={
                <Button icon={CalendarIcon} onClick={() => setIsCalendarOpen((open) => !open)} disclosure>
                  {selectedMonthLabel}
                </Button>
              }
              autofocusTarget="first-node"
              preferredAlignment="right"
              onClose={() => setIsCalendarOpen(false)}
            >
              <Popover.Pane>
                <Popover.Section>
                  <DatePicker
                    month={calendarDate.month}
                    year={calendarDate.year}
                    selected={selectedDate}
                    weekStartsOn={1}
                    onMonthChange={handleMonthChange}
                    onChange={handleDateChange}
                  />
                </Popover.Section>
              </Popover.Pane>
            </Popover>
          </div>
        </header>

        <div className="monthly-staff-ranking__table-wrapper">
          <div className="monthly-staff-ranking__table">
            <div className="monthly-staff-ranking__table-header">
              <div className="monthly-staff-ranking__cell monthly-staff-ranking__cell--rank">Hạng</div>

              <div className="monthly-staff-ranking__cell">Nhân viên</div>

              <div className="monthly-staff-ranking__cell monthly-staff-ranking__cell--number">Số đơn</div>

              <div className="monthly-staff-ranking__cell monthly-staff-ranking__cell--number">Doanh thu</div>
            </div>

            <div className="monthly-staff-ranking__body">
              {rankingRows.map((item) => (
                <div key={item.user_id} className={getRowClassName(item)}>
                  <div className="monthly-staff-ranking__cell monthly-staff-ranking__cell--rank">
                    <RankBadge rank={item.rank} />
                  </div>

                  <div className="monthly-staff-ranking__cell">
                    <div className="monthly-staff-ranking__employee">
                      <div className="monthly-staff-ranking__avatar">
                        {item.user_avatar && !item.isPlaceholder ? (
                          <img src={getAvatarSource(item.user_avatar)} alt={item.display_name} loading="lazy" />
                        ) : (
                          <span>{item.isPlaceholder ? '' : getInitials(item.display_name)}</span>
                        )}
                      </div>

                      <div className="monthly-staff-ranking__employee-info">
                        <div className="monthly-staff-ranking__employee-name">{item.display_name}</div>

                        <div className="monthly-staff-ranking__employee-subtitle">
                          {item.isPlaceholder ? 'Đang chờ dữ liệu' : `Hạng ${item.rank}`}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="monthly-staff-ranking__cell monthly-staff-ranking__cell--number">
                    <div className="monthly-staff-ranking__value">
                      <span className="monthly-staff-ranking__value-icon">
                        <FontAwesomeIcon icon={faReceipt} />
                      </span>

                      <span className="monthly-staff-ranking__value-text">{item.isPlaceholder ? '—' : numberFormatter.format(item.totalOrders)}</span>
                    </div>
                  </div>

                  <div className="monthly-staff-ranking__cell monthly-staff-ranking__cell--number">
                    <div className="monthly-staff-ranking__value">
                      <span className="monthly-staff-ranking__value-icon">
                        <FontAwesomeIcon icon={faSackDollar} />
                      </span>

                      <span className="monthly-staff-ranking__value-text monthly-staff-ranking__value-text--revenue">
                        {item.isPlaceholder ? '—' : currencyFormatter.format(item.totalRevenue)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <footer className="monthly-staff-ranking__footer">
          <span className="monthly-staff-ranking__footer-dot" />
          Bảng xếp hạng tính theo doanh thu đã được chốt lệnh, không phải tổng hợp đơn hàng theo ngày.
        </footer>
      </section>
    </Capacity>
  );
}
